import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../i18n";

import { useSearchParams } from "react-router-dom";
import LoadingSpinner from "../Components/LoadingSpinner";
import { useDispatch, useSelector } from "react-redux";
import { getVendorProfileView } from "../utils/vendorProfile";
import {
  getChatAction,
  getChatListAction,
  getChatMessagesAction,
  markChatMessagesReadAction,
  sendChatSocketMessageAction,
  connectInquirySocketAction,
  disconnectInquirySocketAction,
} from "../actions/chat";

const CHAT_PAGE_SIZE = 20;
const MESSAGE_PAGE_SIZE = 20;
const DOCUMENT_ACCEPT =
  ".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv";

const isMongoInquiryId = (value) =>
  /^[a-f0-9]{24}$/i.test(String(value || "").trim());

const getChatPaginationItems = (currentPage, totalPages) => {
  const pages =
    totalPages <= 5
      ? Array.from({ length: totalPages }, (_, index) => index + 1)
      : [...new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages])]
          .filter((page) => page >= 1 && page <= totalPages)
          .sort((left, right) => left - right);

  return pages.flatMap((page, index) => {
    const previousPage = pages[index - 1];
    const needsEllipsis = index > 0 && page - previousPage > 1;
    return [
      ...(needsEllipsis
        ? [{ type: "ellipsis", key: `ellipsis-${previousPage}-${page}` }]
        : []),
      { type: "page", page },
    ];
  });
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function Message() {
  const { t, text } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatUser = useSelector((state) => state.chat?.vendorProfile);
  const authMe = useSelector((state) => state.auth?.authMe || {});
  const sidebarProfile = getVendorProfileView(chatUser || authMe);
  const {
    items: chats = [],
    pagination: chatPagination = {},
    loading: chatLoading = false,
    error: chatError = "",
    detail,
    messagesByChat = {},
    messagesPaginationByChat = {},
    messagesLoadingByChat = {},
    sendLoadingByChat = {},
    socketReadyByChat = {},
  } = useSelector((state) => state.chat || {});

  const [selectedInquiryId, setSelectedInquiryId] = useState(
    () => searchParams.get("inquiryId") || "",
  );
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState("");
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [chatPage, setChatPage] = useState(1);
  const [messagePage, setMessagePage] = useState(1);
  const chatEndRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const attachmentPickerRef = useRef(null);
  const socketRef = useRef(null);

  const currentPage = Number(chatPagination.page) || Number(chatPage) || 1;
  const totalPages = Math.max(Number(chatPagination.totalPages) || 1, 1);
  const chatPaginationItems = getChatPaginationItems(currentPage, totalPages);
  const hasChatRecords = chats.length > 0 || Number(chatPagination.total) > 0;
  const currentMessages = messagesByChat[selectedInquiryId] || [];
  const messagePagination = messagesPaginationByChat[selectedInquiryId] || {
    page: 1,
    limit: MESSAGE_PAGE_SIZE,
    total: 0,
    totalPages: 1,
  };
  const messageLoading = Boolean(messagesLoadingByChat[selectedInquiryId]);
  const sendLoading = Boolean(sendLoadingByChat[selectedInquiryId]);
  const listChat = chats.find(
    (chat) =>
      chat.inquiryId === selectedInquiryId ||
      chat.displayInquiryId === selectedInquiryId,
  );
  // API and socket requests always use the inquiry document _id.
  const socketInquiryId = listChat?.inquiryMongoId || (isMongoInquiryId(selectedInquiryId) ? selectedInquiryId : "");
  const socketReady = Boolean(socketReadyByChat[socketInquiryId]);
  const socketMessageLoading = Boolean(messagesLoadingByChat[socketInquiryId]);
  const currentChat =
    detail?.inquiryId === selectedInquiryId
      ? { ...(listChat || {}), ...detail }
      : listChat || {
          inquiryId: "",
          name: t("ui.selectConversation"),
          initials: "?",
          role: "Inquiry",
          online: false,
          lastMessage: "",
          time: "",
          unread: 0,
        };
  const filteredChats = chats.filter((chat) =>
    `${chat.name || ""} ${chat.displayInquiryId || ""}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const paginatedChats = filteredChats;

  useEffect(() => {
    dispatch(getChatListAction({ page: chatPage, limit: CHAT_PAGE_SIZE }));
  }, [dispatch, chatPage]);

  useEffect(() => {
    if (!chats.length || selectedInquiryId) return;
    const nextInquiryId = chats[0].inquiryId || "";
    if (!nextInquiryId) return;
    setSelectedInquiryId(nextInquiryId);
    setSearchParams({ inquiryId: nextInquiryId }, { replace: true });
    setMessagePage(1);
  }, [chats, selectedInquiryId, setSearchParams]);

  useEffect(() => {
    if (!socketInquiryId) return;
    dispatch(getChatAction(socketInquiryId));
    if (Number(messagePage) === 1) {
      dispatch(markChatMessagesReadAction(socketInquiryId));
    }
    dispatch(
      getChatMessagesAction(socketInquiryId, {
        page: messagePage,
        limit: MESSAGE_PAGE_SIZE,
      }),
    );
  }, [dispatch, socketInquiryId, messagePage]);

  useEffect(() => {
    if (!socketInquiryId) return undefined;
    const socket = dispatch(connectInquirySocketAction(socketInquiryId));
    socketRef.current = socket;
    return () => {
      dispatch(disconnectInquirySocketAction(socket, socketInquiryId));
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [dispatch, socketInquiryId]);

  useEffect(() => {
    if (!socketInquiryId || socketReady || Number(messagePage) !== 1) {
      return undefined;
    }

    const refreshMessages = () => {
      if (
        document.visibilityState === "visible" &&
        !socketMessageLoading
      ) {
        dispatch(
          getChatMessagesAction(socketInquiryId, {
            page: 1,
            limit: MESSAGE_PAGE_SIZE,
          }),
        );
      }
    };
    const intervalId = window.setInterval(refreshMessages, 5000);
    return () => window.clearInterval(intervalId);
  }, [
    dispatch,
    messagePage,
    socketInquiryId,
    socketMessageLoading,
    socketReady,
  ]);

  useEffect(() => {
    if (!attachmentMenuOpen) return undefined;
    const handleOutsideClick = (event) => {
      if (!attachmentPickerRef.current?.contains(event.target)) {
        setAttachmentMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [attachmentMenuOpen]);

  useEffect(() => {
    if (!attachment || !attachment.type?.startsWith("image/")) {
      setAttachmentPreview("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(attachment);
    setAttachmentPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [attachment]);
  useEffect(() => {
    if (messagePage === 1) {
      chatEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [currentMessages.length, selectedInquiryId, messagePage]);

  const handleSelectChat = (inquiryId) => {
    setSelectedInquiryId(inquiryId);
    setSearchParams({ inquiryId }, { replace: true });
    setMessagePage(1);
    setMessage("");
    setAttachment(null);
    setAttachmentMenuOpen(false);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setChatPage(1);
  };

  const handleLoadOlderMessages = () => {
    const page = Number(messagePagination.page) || messagePage;
    const pages = Math.max(Number(messagePagination.totalPages) || 1, 1);
    if (!messageLoading && page < pages) {
      setMessagePage(page + 1);
    }
  };

  const handleSend = async () => {
    const trimmedMessage = message.trim();
    if ((!trimmedMessage && !attachment) || !socketInquiryId || sendLoading)
      return;
    const sentAttachment = attachment;

    const result = await dispatch(
      sendChatSocketMessageAction(
        socketRef.current,
        socketInquiryId,
        trimmedMessage,
        attachment,
      ),
    );
    if (result?.success) {
      setMessage("");
      setAttachment(null);
      setAttachmentMenuOpen(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
      if (documentInputRef.current) documentInputRef.current.value = "";
      if (sentAttachment) {
        if (Number(messagePage) !== 1) {
          setMessagePage(1);
        } else {
          dispatch(
            getChatMessagesAction(socketInquiryId, {
              page: 1,
              limit: MESSAGE_PAGE_SIZE,
            }),
          );
        }
      }
    }
  };

  const handleAttachmentChange = (event) => {
    setAttachment(event.target.files?.[0] || null);
    setAttachmentMenuOpen(false);
    event.target.value = "";
  };

  const handleAttachmentOption = (type) => {
    if (!socketInquiryId || sendLoading) return;
    setAttachmentMenuOpen(false);
    if (type === "image") {
      attachmentInputRef.current?.click();
    } else {
      documentInputRef.current?.click();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handlePreviousPage = () => {
    setChatPage(Math.max(currentPage - 1, 1));
  };

  const handleNextPage = () => {
    setChatPage(Math.min(currentPage + 1, totalPages));
  };
  return (
    <div className="message-page">
      <section className="message-section">
        <div className="row g-0 message-row-layout">
          <div className="col-3 message-left-col">
            <aside className="message-sidebar">
              {/* LOGGED USER */}
              <div className="message-sidebar-user">
                <div className="chat-avatar large-avatar">
                  {sidebarProfile.initial}
                  {sidebarProfile.imageUrl && (
                    <img
                      src={sidebarProfile.imageUrl}
                      alt={
                        sidebarProfile.name
                          ? `${sidebarProfile.name} ${t("ui.profile")}`
                          : t("ui.profile")
                      }
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  )}
                  <span className="online-status" />
                </div>

                <div className="message-sidebar-user-info">
                  <strong>{sidebarProfile.name || t("ui.vendor")}</strong>
                  <span>{t("ui.vendor")}</span>
                </div>
              </div>

              {/* SEARCH */}
              <div className="chat-search-wrap">
                <img src="/assets/search-icon.svg" alt="" />

                <input
                  type="search"
                  placeholder={t("ui.search")}
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="chat-list">
                {paginatedChats.length > 0 ? (
                  paginatedChats.map((chat) => (
                    <button
                      type="button"
                      key={chat.inquiryMongoId || chat.inquiryId}
                      onClick={() => handleSelectChat(chat.inquiryMongoId || chat.inquiryId)}
                      className={`chat-list-item ${
                        selectedInquiryId === (chat.inquiryMongoId || chat.inquiryId) ? "active" : ""
                      }`}
                    >
                      <div className="chat-avatar">
                        {chat.initials}

                        <span
                          className={`chat-user-status ${
                            chat.online ? "online" : "offline"
                          }`}
                        />
                      </div>

                      <div className="chat-list-content">
                        <div className="chat-list-heading">
                          <strong>{chat.displayInquiryId || "—"}</strong>

                          <span>{chat.time}</span>
                        </div>
                        <div className="chat-list-preview">
                          <p>{chat.lastMessage}</p>

                          {chat.unread > 0 && (
                            <span className="chat-unread">
                              {chat.unread > 99 ? "99+" : chat.unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                ) : chatLoading ? (
                  <LoadingSpinner
                    className="chat-no-results chat-loading-state"
                    label={t("ui.loadingConversations")}
                  />
                ) : (
                  <div className="chat-no-results">
                    {text(chatError || t("ui.noConversationsFound"))}
                  </div>
                )}
              </div>
              {hasChatRecords && (
                <div className="chat-pagination">
                  <button
                    type="button"
                    className="chat-pagination-btn"
                    onClick={handlePreviousPage}
                    disabled={currentPage <= 1 || chatLoading}
                    aria-label={t("ui.previous.page")}
                  >
                    <img src="/assets/chevron-left.svg" alt="" />
                  </button>

                  <div className="chat-pagination-pages">
                    {chatPaginationItems.map((item) =>
                      item.type === "page" ? (
                        <button
                          type="button"
                          key={`page-${item.page}`}
                          className={`chat-page-number ${
                            currentPage === item.page ? "active" : ""
                          }`}
                          onClick={() => setChatPage(item.page)}
                          disabled={chatLoading || currentPage === item.page}
                          aria-current={currentPage === item.page ? "page" : undefined}
                          aria-label={`Page ${item.page}`}
                        >
                          {item.page}
                        </button>
                      ) : (
                        <span
                          key={item.key}
                          className="chat-pagination-ellipsis"
                          aria-hidden="true"
                        >
                          &hellip;
                        </span>
                      ),
                    )}
                  </div>

                  <button
                    type="button"
                    className="chat-pagination-btn"
                    onClick={handleNextPage}
                    disabled={currentPage >= totalPages || chatLoading}
                    aria-label={t("ui.next.page")}
                  >
                    <img src="/assets/chevron-right.svg" alt="" />
                  </button>
                </div>
              )}
            </aside>
          </div>
          <div className="col-9 message-right-col">
            <div className="chat-panel">
              {/* CHAT HEADER */}
              <div className="chat-panel-header">
                <div className="chat-person">
                  <div className="chat-avatar large-avatar admin-avatar">
                    {t("ui.ad")}
                    {currentChat.online && <span className="online-status" />}
                  </div>

                  <div className="chat-person-info">
                    <div className="chat-person-name">
                      <strong>{t("ui.admin")}</strong>

                      {currentChat.online && (
                        <span className="online-label">
                          <i />
                          {t("ui.online")}
                        </span>
                      )}
                    </div>

                    <span className="chat-header-inquiry-id">
                      {selectedInquiryId
                        ? `${t("ui.inquiryId")}: ${currentChat.displayInquiryId || "—"}`
                        : t("ui.selectInquiry")}
                    </span>
                  </div>
                </div>
              </div>
              <div className="chat-conversation">
                {/* {selectedInquiryId && chatError && (
                  <div className="chat-error-message" role="alert">
                    {text(chatError)}
                  </div>
                )} */}
                {selectedInquiryId &&
                  Number(messagePagination.page || 1) <
                    Number(messagePagination.totalPages || 1) && (
                    <button
                      type="button"
                      className="chat-load-older"
                      onClick={handleLoadOlderMessages}
                      disabled={messageLoading}
                    >
                      {messageLoading ? (
                        <LoadingSpinner
                          inline
                          size="small"
                          label={t("ui.loadingMessages")}
                        />
                      ) : (
                        t("ui.loadOlderMessages")
                      )}
                    </button>
                  )}
                <div className="today-divider">
                  <span>{t("ui.today")}</span>
                </div>
                {currentMessages.length === 0 && (
                  <div className="empty-chat">
                    {!messageLoading && (
                      <div className="empty-chat-icon">💬</div>
                    )}

                    {messageLoading ? (
                      <LoadingSpinner
                        className="chat-empty-loading-state"
                        label={t("ui.loadingMessages")}
                      />
                    ) : (
                      <h4>{t("ui.start.a.conversation")}</h4>
                    )}

                    {!messageLoading && (
                      <p>
                        {t("ui.send.a.message.to")} {currentChat.name}
                      </p>
                    )}
                  </div>
                )}

                {currentMessages.map((item) => (
                  <div key={item.id} className={`message-row ${item.sender}`}>
                    <div className="message-content-wrapper">
                      {/* TEXT */}
                      {item.text && (
                        <div
                          className={`message-bubble ${
                            item.sender === "sent"
                              ? "sent-bubble"
                              : "received-bubble"
                          }`}
                        >
                          <p>{item.text}</p>

                          <div className="bubble-meta">
                            <span>{item.time}</span>

                            {item.sender === "sent" && (
                              <span
                                className={
                                  item.status === "read" ? "read-tick" : ""
                                }
                              >
                                ✓✓
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* IMAGE */}
                      {(item.type === "image" ||
                        item.attachment?.type === "image") && (
                        <div className="chat-image-message">
                          <img
                            src={item.attachment?.url || item.url}
                            alt={
                              item.attachment?.fileName ||
                              item.fileName ||
                              t("ui.shared")
                            }
                          />

                          <div className="media-caption">
                            <span>{item.time}</span>

                            {item.sender === "sent" && (
                              <span
                                className={
                                  item.status === "read" ? "read-tick" : ""
                                }
                              >
                                ✓✓
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* FILE */}
                      {(item.type === "file" ||
                        item.attachment?.type === "file") && (
                        <div
                          className={`file-message-bubble ${
                            item.sender === "sent" ? "sent-file" : ""
                          }`}
                        >
                          <div className="chat-file">
                            <div className="chat-file-icon">
                              <img src="/assets/documenti-con.svg" alt="" />
                            </div>

                            <div className="chat-file-info">
                              <strong>
                                {item.attachment?.fileName || item.fileName}
                              </strong>

                              <span>
                                {item.attachment?.fileSize
                                  ? formatFileSize(item.attachment.fileSize)
                                  : item.fileSize || "File"}
                              </span>
                            </div>

                            <a
                              href={item.attachment?.url || item.url}
                              target="_blank"
                              rel="noreferrer"
                              className="file-download-btn"
                              aria-label={t("ui.viewAttachment")}
                              title={t("ui.viewAttachment")}
                            >
                              <img src="/assets/eye.svg" alt="" />
                            </a>
                          </div>

                          <div className="bubble-meta">
                            <span>{item.time}</span>

                            {item.sender === "sent" && (
                              <span
                                className={
                                  item.status === "read" ? "read-tick" : ""
                                }
                              >
                                ✓✓
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <div ref={chatEndRef} />
              </div>

              {/* Message composer */}

              {/* ===============================
                  INPUT FIXED BOTTOM
              =============================== */}

              {attachment && (
                <div className="attachment-preview-area">
                  {attachment.type?.startsWith("image/") &&
                  attachmentPreview ? (
                    <div className="selected-image-preview">
                      <img
                        className="attachment-image-preview"
                        src={attachmentPreview}
                        alt={t("ui.selected.attachment.preview")}
                      />
                      <div>
                        <strong>{attachment.name}</strong>
                        <span>{formatFileSize(attachment.size || 0)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="selected-file-preview">
                      <span className="selected-file-icon">
                        <img src="/assets/documenti-con.svg" alt="" />
                      </span>
                      <div>
                        <strong>{attachment.name}</strong>
                        <span>{formatFileSize(attachment.size || 0)}</span>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    className="remove-attachment-btn"
                    aria-label={t("ui.remove.attachment")}
                    title={t("ui.remove.attachment")}
                    onClick={() => setAttachment(null)}
                    disabled={sendLoading}
                  >
                    &times;
                  </button>
                </div>
              )}

              <div className="chat-input-area">
                <div className="chat-input-box">
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    className="chat-hidden-file-input"
                    accept="image/*"
                    onChange={handleAttachmentChange}
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <input
                    ref={documentInputRef}
                    type="file"
                    className="chat-hidden-file-input"
                    accept={DOCUMENT_ACCEPT}
                    onChange={handleAttachmentChange}
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <div className="attachment-picker" ref={attachmentPickerRef}>
                    <button
                      type="button"
                      className="attach-btn"
                      title={t("ui.attach.a.file")}
                      aria-label={t("ui.attach.a.file")}
                      aria-expanded={attachmentMenuOpen}
                      aria-haspopup="menu"
                      onClick={() => setAttachmentMenuOpen((open) => !open)}
                      disabled={!socketInquiryId || sendLoading}
                    >
                      <img src="/assets/attachment.svg" alt="" />
                    </button>
                    {attachmentMenuOpen && (
                      <div className="attachment-menu" role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => handleAttachmentOption("image")}
                        >
                          <span className="attachment-menu-icon">
                            <img
                              className="chat-menu-icon"
                              src="/assets/imageicon.svg"
                              alt=""
                            />
                          </span>
                          <span>
                            <strong>{t("ui.uploadImage")}</strong>
                          </span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => handleAttachmentOption("document")}
                        >
                          <span className="attachment-menu-icon">
                            <img
                              className="chat-menu-icon"
                              src="/assets/documenti-con.svg"
                              alt=""
                            />
                          </span>
                          <span>
                            <strong>{t("ui.uploadDocument")}</strong>
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder={t("ui.send.a.message")}
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <button
                  type="button"
                  className="chat-send-btn"
                  title={t("ui.send")}
                  onClick={handleSend}
                  disabled={
                    (!message.trim() && !attachment) ||
                    sendLoading ||
                    !socketInquiryId
                  }
                >
                  <img src="/assets/send.svg" alt="" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
